import { AnimationCallbackEvent, ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, INJECTOR, OnInit, untracked, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { EventType, Router, RouterModule } from '@angular/router';
import { consentActions, consentFeature, CookiePolicyBannerComponent, ImportsModule, ScreenSize, sidebarActions, uiFeature } from '@mapa/ngx';
import { Store } from '@ngrx/store';
import { animate } from 'motion';
import { filter, map } from 'rxjs';
import { AppFooterComponent } from './footer/footer.component';
import { AppHeaderComponent } from './header/header.component';
import { PublicSidebarComponent } from "./public-sidebar/public-sidebar.component";

@Component({
  imports: [
    ImportsModule,
    RouterModule,
    MatSidenavModule,
    AppHeaderComponent,
    AppFooterComponent,
    PublicSidebarComponent,
    CookiePolicyBannerComponent
  ],
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class]': 'class$()'
  }
})
export class AppComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly injector = inject(INJECTOR);
  private readonly dRef = inject(DestroyRef);
  private readonly router = inject(Router);

  private readonly appSidenav$ = viewChild.required<MatSidenav>('appSidenav');

  private readonly ui$ = this.store.selectSignal(uiFeature.selectUiState);

  private readonly screenSize$ = computed(() => this.ui$().screenSize);
  protected readonly minimalUi$ = computed(() => this.ui$().minimalUi);
  protected readonly showSplashScreen$ = computed(() => this.ui$().showSplashScreen);
  protected readonly class$ = computed(() => {
    const list = [
      'flex',
      'flex-col',
      'min-h-screen'
    ];

    const routeClass = this.ui$().routeClass;
    if (routeClass)
      list.push(`route-${routeClass}`);
    return list;
  });

  protected readonly sideBarState = (() => {
    const state$ = this.store.selectSignal(uiFeature.selectSidebar);

    const mode$ = computed(() => {
      return state$().visible ? 'side' : 'over';
    });
    const opened$ = computed(() => state$().expanded);

    return { mode$, opened$ } as const;
  })();

  protected readonly cookieBanner = {
    show$: (() => {
      this.store.dispatch(consentActions.init());
      const hasInteracted$ = this.store.selectSignal(consentFeature.userHasInteracted);
      return computed(() => !hasInteracted$());
    })(),
    animateOnLeave: async (event: AnimationCallbackEvent) => {
      await animate(
        event.target,
        { y: '100%' },
        { ease: 'easeOut', duration: 0.15 },
      );
      event.animationComplete();
    }
  } as const;


  private updateSidebarIfNeeded() {
    const largeScreenSizes: ScreenSize[] = ['4xl', '5xl', '6xl', '7xl', '8xl', '9xl', '10xl'];

    const isLargeScreen$ = computed(() => largeScreenSizes.includes(this.screenSize$()));

    effect(() => {
      if (isLargeScreen$()) {
        untracked(() => {
          this.store.dispatch(sidebarActions.hide());
          this.store.dispatch(sidebarActions.contract());
        });
      }
    }, { injector: this.injector });

    effect(() => {
      this.minimalUi$();
      untracked(() => {
        this.store.dispatch(sidebarActions.hide());
        this.store.dispatch(sidebarActions.contract());
      });
    }, { injector: this.injector });

    this.appSidenav$().closedStart
      .pipe(takeUntilDestroyed(this.dRef))
      .subscribe(() => {
        this.store.dispatch(sidebarActions.contract());
      });

    this.router.events
      .pipe(
        map(event => {
          switch (event.type) {
            case EventType.NavigationEnd:
            case EventType.NavigationCancel:
            case EventType.NavigationError: return true;
            default: return false;
          }
        }),
        filter(Boolean),
        takeUntilDestroyed(this.dRef)
      )
      .subscribe(() => {
        if (this.sideBarState.mode$() === 'over')
          this.store.dispatch(sidebarActions.contract());
      });
  }

  ngOnInit() {
    this.updateSidebarIfNeeded();
  }
}
